import { AgreeToTermsPolicy, type SignIn } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, useId } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import WebAuthnContext from '@/Providers/WebAuthnContextProvider/WebAuthnContext';
import { PasswordInputField, SmartInputField } from '@/components/InputFields';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import usePasswordSignIn from '@/hooks/use-password-sign-in';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import { usePlatformCopy } from '@/shared/platform/copy';
import CaptchaBox from '@/containers/CaptchaBox';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

import styles from './index.module.scss';
import useOnSubmit from './use-on-submit';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly signInMethods: SignIn['methods'];
};

type FormState = {
  identifier: IdentifierInputValue;
  password: string;
};

/**
 * Employee email domains may match an enterprise SSO connector. LEXMOUNT keeps
 * password / email-code available and only offers employee SSO via the dedicated
 * footer button — do not auto-force the SSO-only form.
 */
const IdentifierSignInForm = ({ className, autoFocus, signInMethods }: Props) => {
  const { t } = useTranslation();
  const copy = usePlatformCopy();
  const passwordId = useId();
  const submitMethod = useRef<'password' | 'code'>('password');
  const { errorMessage: passwordError, clearErrorMessage: clearPasswordError, onSubmit: passwordSignIn } = usePasswordSignIn();
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();
  const codeMethods = useMemo(() => signInMethods.map(method => ({ ...method, isPasswordPrimary: false })), [signInMethods]);
  const { onSubmit: codeSignIn, errorMessage: codeError, clearErrorMessage: clearCodeError } = useOnSubmit(codeMethods);
  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit(signInMethods);
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { isPasskeyFlowProcessing } = useContext(WebAuthnContext);

  const enabledSignInMethods = useMemo(
    () => signInMethods.map(({ identifier }) => identifier),
    [signInMethods]
  );

  const prefilledIdentifier = usePrefilledIdentifier({
    enabledIdentifiers: enabledSignInMethods,
  });

  const {
    watch,
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onChange',
    defaultValues: {
      identifier: prefilledIdentifier,
      password: '',
    },
  });

  useEffect(() => {
    const subscription = watch(({ identifier }) => {
      if (identifier?.type && typeof identifier.value === 'string') {
        setIdentifierInputValue({ type: identifier.type, value: identifier.value });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setIdentifierInputValue]);

  // Keep the hook for side-effects Logto expects, but never switch the form to SSO-only.
  useSingleSignOnWatch(watch('identifier'));

  const currentIdentifier = watch('identifier');
  const currentMethod = signInMethods.find(method => method.identifier === currentIdentifier?.type);
  const showsPassword = Boolean(currentMethod?.password);

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (isPasskeyFlowProcessing || isSubmitting) {
        return;
      }

      submitMethod.current = (event?.nativeEvent as SubmitEvent | undefined)?.submitter?.getAttribute('data-method') === 'code' ? 'code' : 'password';
      clearErrorMessage();
      clearPasswordError();
      clearCodeError();

      await handleSubmit(async ({ identifier: { type, value }, password }) => {
        if (!type) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
          return;
        }

        if (submitMethod.current === 'code' && currentMethod?.verificationCode) {
          await codeSignIn(type, value);
        } else if (currentMethod?.password) {
          await passwordSignIn({ identifier: { type, value }, password });
        } else {
          await onSubmit(type, value);
        }
      })(event);
    },
    [
      agreeToTermsPolicy,
      clearErrorMessage,
      handleSubmit,
      onSubmit,
      setIdentifierInputValue,
      termsValidation,
      isPasskeyFlowProcessing,
      isSubmitting,
      clearPasswordError, clearCodeError, currentMethod, codeSignIn, passwordSignIn,
    ]
  );

  return (
    <form className={classNames(styles.form, className, 'platform-signin-form')} onSubmit={onSubmitHandler}>
      <Controller
        control={control}
        name="identifier"
        rules={{
          validate: ({ type, value }) => {
            if (!type || !value) {
              return getGeneralIdentifierErrorMessage(enabledSignInMethods, 'required');
            }

            const errorMessage = validateIdentifierField(type, value);

            return errorMessage
              ? getGeneralIdentifierErrorMessage(enabledSignInMethods, 'invalid')
              : true;
          },
        }}
        render={({ field, formState: { defaultValues } }) => (
          <SmartInputField
            autoFocus={autoFocus}
            className={styles.inputField}
            {...field}
            isDanger={!!errors.identifier || !!errorMessage}
            errorMessage={errors.identifier?.message}
            enabledTypes={enabledSignInMethods}
            defaultValue={defaultValues?.identifier?.value}
          />
        )}
      />

      {showsPassword && <div className="platform-password-field">
        <div className="platform-password-heading">
          <label className="platform-input-label" htmlFor={passwordId}>{t('input.password')}</label>
          {isForgotPasswordEnabled && <ForgotPasswordLink className="platform-forgot-password" identifier={currentIdentifier?.type} value={currentIdentifier?.value}/>}
        </div>
        <PasswordInputField id={passwordId} autoComplete="current-password" isDanger={!!errors.password} errorMessage={errors.password?.message}
          {...register('password', { validate: value => submitMethod.current === 'code' || !showsPassword || Boolean(value) || t('error.password_required') })}/>
      </div>}
      {(errorMessage || passwordError || codeError) && <div role="alert"><ErrorMessage className={styles.formErrors}>{errorMessage || passwordError || codeError}</ErrorMessage></div>}

      <TermsAndPrivacyCheckbox
        className={classNames(
          styles.terms,
          agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && styles.hidden
        )}
      />

      <CaptchaBox />
      <Button
        name="submit"
        title="action.sign_in"
        htmlType="submit"
        isLoading={isSubmitting || isPasskeyFlowProcessing}
      />

      {currentMethod?.verificationCode && <button className="platform-code-signin" type="submit" data-method="code" disabled={isSubmitting || isPasskeyFlowProcessing}>{copy.codeSignIn}</button>}
      <input hidden type="submit" />
    </form>
  );
};

export default IdentifierSignInForm;
